import { useMediaQuery, useTheme } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { COLORS } from './constants';

import type { LucideIcon } from 'lucide-react';

export interface PriceWaterfallDeduction {
  label: string;
  /** Positive dollar amount this deduction removes from the running price. */
  amount: number;
  icon?: LucideIcon;
  /** Accent color for the bar, icon badge, and highlighted tile — defaults to neutral gray. */
  color?: string;
  /** Highlights this tile/bar in solid style instead of the default outlined tile. */
  highlight?: boolean;
  /** Guidance shown when the tile is expanded — how to structure this in an offer. */
  strategyNote?: string;
  /** Whether this deduction counts toward the running total by default. */
  defaultEnabled?: boolean;
}

export interface PriceWaterfallReferenceLine {
  label: string;
  value: number;
}

interface PriceWaterfallChartProps {
  title?: string;
  subtitle?: string;
  /** Chart's starting price — should be a defensible anchor (e.g. an
   * advisor's estimate), not necessarily the raw list price. */
  startLabel: string;
  startValue: number;
  deductions: PriceWaterfallDeduction[];
  /** Secondary reference line, e.g. showing where list price falls relative
   * to the negotiation bars. Purely informational — not part of the deduction math. */
  referenceLines?: PriceWaterfallReferenceLine[];
}

const WIDTH = 680;
const HEIGHT = 360;
const PAD_LEFT = 66;
const PAD_RIGHT = 30;
const PAD_TOP = 26;
const PAD_BOTTOM = 46;

const fmtPrice = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const fmtPriceCompact = (n: number) => {
  if (Math.abs(n) >= 1_000_000) {
    const millions = n / 1_000_000;
    return `$${millions.toFixed(millions % 1 === 0 ? 0 : 2)}M`;
  }
  return `$${(n / 1000).toFixed(0)}k`;
};

/** Animates a numeric value toward its latest target with ease-out timing. */
const useCountUp = (target: number, duration = 450) => {
  const [display, setDisplay] = useState(target);
  const fromRef = useRef(target);

  useEffect(() => {
    const from = fromRef.current;
    const to = target;
    if (from === to) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - p) ** 3;
      setDisplay(from + (to - from) * eased);
      if (p < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return display;
};

const ToggleSwitch = ({ on, onColor, onClick }: { on: boolean; onColor: string; onClick: () => void }) => (
  <div
    role="switch"
    aria-checked={on}
    onClick={onClick}
    style={{
      width: 36, height: 20, borderRadius: 10, flexShrink: 0, cursor: 'pointer',
      background: on ? onColor : 'var(--tx-fill-3)',
      position: 'relative', transition: 'background 0.2s ease',
    }}>
    <div style={{
      position: 'absolute', width: 14, height: 14, borderRadius: '50%', background: '#333333',
      top: 3, left: on ? 19 : 3, transition: 'left 0.2s ease',
    }} />
  </div>
);

/** Ring showing what share of the starting price has been negotiated off — the
 * ring's scale tops out at MAX_PCT so a realistic (single-digit) reduction still reads as a
 * meaningful arc instead of a nearly-empty circle. */
const MAX_GAUGE_PCT = 12;

const DealQualityGauge = ({ pct, size = 84 }: { pct: number; size?: number }) => {
  const animatedPct = useCountUp(pct);
  const strokeW = 7;
  const r = (size - strokeW) / 2;
  const circ = 2 * Math.PI * r;
  const fraction = Math.min(1, Math.max(0, animatedPct / MAX_GAUGE_PCT));
  const offset = circ * (1 - fraction);
  const ringColor = pct >= 8 ? '#C0FF02' : pct >= 4 ? COLORS.accent1 : COLORS.textDim;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={strokeW} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={ringColor} strokeWidth={strokeW}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke 0.3s ease' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ fontSize: 17, fontWeight: 600, color: COLORS.text, fontVariantNumeric: 'tabular-nums' }}>
          {animatedPct.toFixed(1)}%
        </span>
        <span style={{ fontSize: 10, fontWeight: 600, color: COLORS.textDim, letterSpacing: '0.05em' }}>
          Negotiated
        </span>
      </div>
    </div>
  );
};

const AnimatedPrice = ({ value, compact }: { value: number; compact?: boolean }) => {
  const animated = useCountUp(value);
  return <>{compact ? fmtPriceCompact(animated) : fmtPrice(animated)}</>;
};

export const PriceWaterfallChart = ({
  title, subtitle, startLabel, startValue, deductions, referenceLines = [],
}: PriceWaterfallChartProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [enabled, setEnabled] = useState<boolean[]>(
    () => deductions.map((d) => d.defaultEnabled ?? true),
  );
  const [expanded, setExpanded] = useState<boolean[]>(() => deductions.map(() => false));
  const [hovered, setHovered] = useState<number | null>(null);

  const toggleEnabled = (i: number) =>
    setEnabled((prev) => prev.map((v, idx) => (idx === i ? !v : v)));
  const toggleExpanded = (i: number) =>
    setExpanded((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  // Fixed vertical scale spanning the widest possible outcome (all deductions
  // applied) so the axis doesn't rescale/jump as bars are toggled — only bar
  // heights change.
  const fullyReducedValue = startValue - deductions.reduce((sum, d) => sum + d.amount, 0);
  const allValues = [startValue, fullyReducedValue, ...referenceLines.map((r) => r.value)];
  const domainMax = Math.max(...allValues);
  const domainMin = Math.min(...allValues);
  const pad = (domainMax - domainMin) * 0.15 || domainMax * 0.05;
  const yMax = domainMax + pad;
  const yMin = domainMin - pad;

  const plotW = WIDTH - PAD_LEFT - PAD_RIGHT;
  const yFor = (v: number) => PAD_TOP + (HEIGHT - PAD_TOP - PAD_BOTTOM) * (1 - (v - yMin) / (yMax - yMin));
  const floorY = HEIGHT - PAD_BOTTOM;

  // Running totals — only enabled deductions count toward the effective price.
  const runningLevels: number[] = [startValue];
  deductions.forEach((d, i) => {
    const prev = runningLevels[runningLevels.length - 1];
    runningLevels.push(enabled[i] ? prev - d.amount : prev);
  });
  const effectivePrice = runningLevels[runningLevels.length - 1];
  const dealQualityPct = startValue > 0 ? ((startValue - effectivePrice) / startValue) * 100 : 0;

  const columns = deductions.length + 2;
  const colW = plotW / columns;
  const barW = Math.min(64, colW * 0.55);
  const cxFor = (i: number) => PAD_LEFT + (i + 0.5) * colW;

  const gridCount = 4;
  const gridValues = Array.from({ length: gridCount + 1 }, (_, i) => yMin + ((yMax - yMin) * i) / gridCount);

  const primaryRef = referenceLines[0];
  const belowRefAmount = primaryRef ? primaryRef.value - effectivePrice : null;

  return (
    <div style={{
      background: COLORS.card, borderRadius: 25,
      backgroundImage: 'var(--tx-surface-light)', boxShadow: 'var(--tx-shadow)', padding: '20px',
    }}>
      {title && (
        <div style={{ fontSize: 15, fontWeight: 300, color: COLORS.text, marginBottom: 6 }}>
          {title}
        </div>
      )}
      {subtitle && (
        <div style={{ fontSize: 12, color: COLORS.textDim, lineHeight: 1.5, maxWidth: 480, marginBottom: isMobile ? 35 : 20 }}>
          {subtitle}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: isMobile ? 0 : 16 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.textDim, letterSpacing: '0.06em', marginBottom: 4 }}>
            {startLabel}
          </div>
          <div style={{ fontSize: 28, fontWeight: 600, color: COLORS.text, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
            <AnimatedPrice value={startValue} />
          </div>
        </div>

        <div style={{ transform: isMobile ? 'translateY(40px)' : 'none' }}>
          <DealQualityGauge pct={dealQualityPct} />
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.textDim, letterSpacing: '0.06em', marginBottom: 4 }}>
            Your effective price
          </div>
          <div style={{ fontSize: 28, fontWeight: 600, color: COLORS.text, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
            <AnimatedPrice value={effectivePrice} />
          </div>
          {primaryRef && belowRefAmount != null && belowRefAmount > 0 && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 4,
              padding: '3px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.06)',
            }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.textDim }}>
                <AnimatedPrice value={belowRefAmount} /> below {primaryRef.label.toLowerCase()}
              </span>
            </div>
          )}
        </div>
      </div>

      <svg
        width="100%" viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        style={{ overflow: 'visible', display: 'block', marginTop: isMobile ? 15 : 0 }}>
        <defs>
          <linearGradient id="wfGradAnchor" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.16)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.05)" />
          </linearGradient>
          <linearGradient id="wfGradEffective" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={COLORS.text} stopOpacity={1} />
            <stop offset="100%" stopColor={COLORS.text} stopOpacity={0.75} />
          </linearGradient>
          {deductions.map((d, i) => (
            <linearGradient key={i} id={`wfGradDeduction-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={d.color ?? COLORS.textDim} stopOpacity={1} />
              <stop offset="100%" stopColor={d.color ?? COLORS.textDim} stopOpacity={0.72} />
            </linearGradient>
          ))}
        </defs>

        {/* Grid lines + y-axis labels */}
        {gridValues.map((v, i) => {
          const y = yFor(v);
          return (
            <g key={i}>
              <line x1={PAD_LEFT} y1={y} x2={WIDTH - PAD_RIGHT} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth={1} />
              <text x={PAD_LEFT - 10} y={y + 4} textAnchor="end" fontSize={10} fill={COLORS.textDim}>
                {fmtPriceCompact(v)}
              </text>
            </g>
          );
        })}

        {/* Secondary context line (e.g. list price) */}
        {referenceLines.map((ref, i) => {
          const y = yFor(ref.value);
          return (
            <g key={i}>
              <line x1={PAD_LEFT} y1={y} x2={WIDTH - PAD_RIGHT} y2={y} stroke={COLORS.accent2} strokeWidth={1.5} strokeDasharray="5,4" />
              <rect
                x={(PAD_LEFT + WIDTH - PAD_RIGHT) / 2 - 72} y={y - 20} width={144} height={16} rx={4}
                fill={COLORS.card}
              />
              <text x={(PAD_LEFT + WIDTH - PAD_RIGHT) / 2} y={y - 8} textAnchor="middle" fontSize={10} fontWeight={600} fill={COLORS.accent2}>
                {ref.label}: {fmtPrice(ref.value)}
              </text>
            </g>
          );
        })}

        {/* Win-gap shading between list price and the effective-price column */}
        {primaryRef && belowRefAmount != null && belowRefAmount > 0 && (
          <rect
            x={cxFor(columns - 1) - barW / 2 - 6}
            y={yFor(primaryRef.value)}
            width={barW + 12}
            height={yFor(effectivePrice) - yFor(primaryRef.value)}
            fill="#C0FF02" fillOpacity={0.08}
            style={{ transition: 'height 0.3s ease, y 0.3s ease' }}
          />
        )}

        {/* Anchor bar — full column from starting price down to the floor */}
        <rect
          x={cxFor(0) - barW / 2} y={yFor(startValue)} width={barW}
          height={floorY - yFor(startValue)} rx={4}
          fill="url(#wfGradAnchor)" stroke={COLORS.textDim} strokeWidth={1}
        />
        <text x={cxFor(0)} y={yFor(startValue) - 10} textAnchor="middle" fontSize={12} fontWeight={600} fill={COLORS.text}>
          {fmtPriceCompact(startValue)}
        </text>

        {/* Deduction bars — floating segments between the running carry levels.
            Toggling off collapses a bar to a thin line, no interpolation needed. */}
        {deductions.map((d, i) => {
          const top = runningLevels[i];
          const bottom = runningLevels[i + 1];
          const isOn = enabled[i];
          const isHovered = hovered === i;
          const barTop = yFor(Math.max(top, bottom));
          const barBottom = yFor(Math.min(top, bottom));
          const height = Math.max(barBottom - barTop, isOn ? 0 : 2);
          const prevCx = cxFor(i) + barW / 2;
          const cx = cxFor(i + 1);
          return (
            <g
              key={i}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered((h) => (h === i ? null : h))}
              style={{ cursor: 'default' }}>
              {/* Invisible wider hit-area so hovering near a thin/disabled bar still registers */}
              <rect x={cx - colW / 2} y={PAD_TOP} width={colW} height={floorY - PAD_TOP} fill="transparent" />
              <line
                x1={prevCx} x2={cx - barW / 2} y1={yFor(top)} y2={yFor(top)}
                stroke={COLORS.textDim} strokeWidth={1.5} strokeDasharray="3,4" opacity={0.7}
                style={{ transition: 'y1 0.3s ease, y2 0.3s ease' }}
              />
              <rect
                x={cx - barW / 2} y={barTop} width={barW} height={height} rx={4}
                fill={isOn ? `url(#wfGradDeduction-${i})` : 'transparent'}
                stroke={isHovered ? '#fff' : isOn ? 'none' : COLORS.textDim}
                strokeWidth={isHovered ? 2 : 1}
                strokeDasharray={isOn ? undefined : '3,3'}
                style={{ transition: 'y 0.3s ease, height 0.3s ease, stroke 0.15s ease' }}
              />
              {isOn && (
                <text
                  x={cx} y={(barTop + barTop + height) / 2 + 4} textAnchor="middle"
                  fontSize={11} fontWeight={600} fill={COLORS.bg}
                  style={{ transition: 'y 0.3s ease' }}>
                  −{fmtPriceCompact(d.amount)}
                </text>
              )}
            </g>
          );
        })}

        {/* Connector from the last deduction bar to the effective bar */}
        <line
          x1={cxFor(columns - 2) + barW / 2} x2={cxFor(columns - 1) - barW / 2}
          y1={yFor(effectivePrice)} y2={yFor(effectivePrice)}
          stroke={COLORS.textDim} strokeWidth={1.5} strokeDasharray="3,4" opacity={0.7}
          style={{ transition: 'y1 0.3s ease, y2 0.3s ease' }}
        />

        {/* Effective bar — full column from effective price down to the floor */}
        <rect
          x={cxFor(columns - 1) - barW / 2} y={yFor(effectivePrice)} width={barW}
          height={floorY - yFor(effectivePrice)} rx={4}
          fill="url(#wfGradEffective)"
          style={{ transition: 'y 0.3s ease, height 0.3s ease' }}
        />
        <text x={cxFor(columns - 1)} y={yFor(effectivePrice) - 10} textAnchor="middle" fontSize={12} fontWeight={600} fill={COLORS.text}
          style={{ transition: 'y 0.3s ease' }}>
          {fmtPriceCompact(effectivePrice)}
        </text>
      </svg>

      {/* Deduction tiles — icon, toggle, expandable offer-strategy note */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
        {deductions.map((d, i) => {
          const Icon = d.icon;
          const accent = d.color ?? COLORS.textDim;
          const isOn = enabled[i];
          const isOpen = expanded[i];
          const isHovered = hovered === i;
          return (
            <div
              key={i}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered((h) => (h === i ? null : h))}
              style={{
                borderRadius: 14, overflow: 'hidden',
                background: isHovered ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
                border: 0,
                opacity: isOn ? 1 : 0.55, transition: 'opacity 0.2s ease, background 0.15s ease, border-color 0.15s ease',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px' }}>
                {Icon && (
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: `${accent}1f`,
                  }}>
                    <Icon size={18} color={accent} />
                  </div>
                )}
                <div
                  onClick={() => d.strategyNote && toggleExpanded(i)}
                  style={{
                    flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6,
                    cursor: d.strategyNote ? 'pointer' : 'default',
                  }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{d.label}</span>
                  {d.strategyNote && (
                    <ChevronDown
                      size={13} color={COLORS.textDim}
                      style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                    />
                  )}
                </div>
                <div style={{
                  fontSize: 15, fontWeight: 600, flexShrink: 0,
                  color: d.highlight && isOn ? 'var(--tx-lime-ink)' : COLORS.text,
                }}>
                  {isOn && '−'}<AnimatedPrice value={isOn ? d.amount : 0} />
                </div>
                <ToggleSwitch on={isOn} onColor={accent} onClick={() => toggleEnabled(i)} />
              </div>
              {isOpen && d.strategyNote && (
                <div style={{
                  padding: '0 14px 14px 62px', fontSize: 12, color: COLORS.textDim, lineHeight: 1.5,
                }}>
                  {d.strategyNote}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PriceWaterfallChart;
